import { api } from '../api/client'
import type { Subject, TimelineItem } from '../api/types'
import { relativeDays } from '../lib/format'
import { subjectKey } from '../lib/subject'
import { useAsync } from '../lib/useAsync'
import { Icon } from './Icon'
import { ErrorState, Loading } from './States'

const ICON: Record<string, string> = {
  note: 'edit_note', call: 'call', sms: 'sms', email: 'mail', stage: 'swap_horiz',
  consent: 'verified_user', task: 'task_alt', playbook: 'bolt', system: 'info',
  status: 'swap_horiz', mail: 'local_post_office', appointment: 'event', whatsapp: 'chat',
}

function toneFor(item: TimelineItem): { background: string; color: string } {
  const o = item.outcome ?? ''
  if (['blocked', 'failed', 'undelivered'].includes(o))
    return { background: 'var(--danger-tint)', color: 'var(--danger)' }
  if (o === 'simulated') return { background: 'var(--warning-tint)', color: 'var(--warning)' }
  if (['sent', 'delivered', 'connected', 'booked'].includes(o) || item.direction === 'inbound')
    return { background: 'var(--positive-tint)', color: 'var(--positive)' }
  if (item.kind === 'stage' || item.kind === 'playbook')
    return { background: 'var(--accent-tint)', color: 'var(--accent)' }
  return { background: 'oklch(0.96 0.004 265)', color: 'var(--ink-2)' }
}

/** What happened after an email went out. Opens and clicks are shown apart and
 *  never added together: an open is reported by the recipient's mail app loading a
 *  one-pixel image, which Apple and Gmail do on their own and which a client with
 *  images off never does at all. A click is a person deciding to do something. */
function engagement(item: TimelineItem): string {
  if (item.kind !== 'email' || item.direction === 'inbound') return ''
  const opens = Number(item.meta?.opens ?? 0)
  const clicks = Number(item.meta?.clicks ?? 0)
  if (!opens && !clicks) return ''
  const parts = []
  if (opens) parts.push(`Opened${opens > 1 ? ` ${opens}×` : ''}`)
  if (clicks) parts.push(`clicked a link${clicks > 1 ? ` ${clicks}×` : ''}`)
  const last = typeof item.meta?.last_open === 'string' ? item.meta.last_open : ''
  return parts.join(' · ') + (last ? ` · last ${relativeDays(last)}` : '')
}

/** The history list on its own, for callers that already have the items. */
export function TimelineList({ items, emptyText = 'Nothing logged yet.' }: {
  items: TimelineItem[]
  emptyText?: string
}) {
  if (!items.length) return <p className="quiet" style={{ margin: '4px 0' }}>{emptyText}</p>
  return (
    <div>
      {items.map((item) => {
        const reason = typeof item.meta?.reason === 'string' ? item.meta.reason : ''
        const opened = engagement(item)
        const icon = item.direction === 'inbound' ? 'reply' : ICON[item.kind] ?? 'circle'
        return (
          <div className="tl-item" key={item.id}>
            <span className="tl-dot" style={toneFor(item)}>
              <Icon name={icon} size={16} />
            </span>
            <div className="tl-main">
              <div className="tl-top">
                <span className="tl-title">{item.title}</span>
                <span
                  className="tl-time"
                  title={new Date(item.occurred_at).toLocaleString('en-US')}
                >
                  {relativeDays(item.occurred_at)}
                </span>
              </div>
              {item.body ? <div className="tl-body">{item.body}</div> : null}
              {reason && item.outcome !== 'sent' ? <div className="tl-meta">{reason}</div> : null}
              {opened ? (
                <div className="tl-meta tl-opened" title="Opens are approximate — some mail apps preload images and others block them. Clicks are exact.">
                  <Icon name="drafts" size={13} /> {opened}
                </div>
              ) : null}
              {item.by ? <div className="tl-meta">by {item.by}</div> : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** Fetches and renders a subject's merged history. Bump `refreshKey` after a write. */
export function Timeline({ subject, refreshKey = 0, emptyText }: {
  subject: Subject
  refreshKey?: number
  emptyText?: string
}) {
  const { data, error, loading, reload } = useAsync(
    () => api.activity(subject),
    [subjectKey(subject), refreshKey],
  )
  if (loading && !data) return <Loading label="Loading history…" />
  if (error) return <ErrorState error={error} onRetry={reload} />
  return <TimelineList items={data?.items ?? []} emptyText={emptyText} />
}
